import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, message, Select, Spin, DatePicker, Row, Col } from 'antd';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import * as XLSX from 'xlsx';
import dayjs, { Dayjs } from 'dayjs';

const { Option } = Select;
const API_URL = process.env.REACT_APP_API_URL;

// Cache for reward types to avoid unnecessary API calls
let rewardTypesCache: any[] | null = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface RewardType {
  rewardTypeId: string;
  rewardName: string;
  parentCategoryName: string;
}

const DownloadRewardsReport: React.FC = React.memo(() => {
  const { instance, accounts } = useMsal();
  const [rewardTypes, setRewardTypes] = useState<RewardType[]>([]);
  const [selectedRewards, setSelectedRewards] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  // Memoize the fetchToken function to prevent recreation on every render
  const fetchToken = useCallback(async () => {
    try {
      const res = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      return res.idToken;
    } catch (error) {
      console.error('Failed to fetch token:', error);
      throw error;
    }
  }, [instance, accounts]);

  // Check if cache is still valid
  const isCacheValid = useCallback(() => {
    return rewardTypesCache && 
           cacheTimestamp && 
           (Date.now() - cacheTimestamp) < CACHE_DURATION;
  }, []);

  // Fetch reward types with caching
  const fetchRewardTypes = useCallback(async () => {
    // Return cached data if available and valid
    if (isCacheValid() && rewardTypesCache) {
      setRewardTypes(rewardTypesCache);
      return;
    }

    setLoading(true);
    try {
      const token = await fetchToken();
      const response = await axios.get(`${API_URL}/api/Rewards`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // Update cache
      rewardTypesCache = response.data;
      cacheTimestamp = Date.now();
      
      setRewardTypes(response.data);
      console.log('Fetched reward types:', response.data);
    } catch (error) {
      console.error('Failed to fetch reward types:', error);
      message.error('Failed to fetch reward types.');
    } finally {
      setLoading(false);
    }
  }, [fetchToken, isCacheValid]);

  useEffect(() => {
    fetchRewardTypes();
  }, [fetchRewardTypes]);

  // Memoize the reward options to prevent recalculation on every render
  const rewardOptions = useMemo(() => {
    const options = [
      <Option key="all" value="all">
        All
      </Option>
    ];
    
    // Sort reward types alphabetically by rewardName
    const sortedRewardTypes = [...rewardTypes].sort((a, b) => 
      a.rewardName.localeCompare(b.rewardName)
    );
    
    const rewardTypeOptions = sortedRewardTypes.map((reward) => {
      // Format display name to include parent category in brackets if available
      const displayName = reward.parentCategoryName 
        ? `${reward.rewardName} (${reward.parentCategoryName})`
        : reward.rewardName;
      
      return (
        <Option key={reward.rewardTypeId} value={reward.rewardTypeId}>
          {displayName}
        </Option>
      );
    });
    
    return [...options, ...rewardTypeOptions];
  }, [rewardTypes]);

  // Memoize the filter function
  const filterOption = useCallback((input: string, option: any) => {
    return option && option.children
      ? option.children.toString().toLowerCase().includes(input.toLowerCase())
      : false;
  }, []);

  // Memoize the selection handler
  const handleSelectionChange = useCallback((value: string[]) => {
    // Check if "All" is selected
    if (value.includes('all')) {
      // If "All" is selected, select all reward type IDs
      const allRewardIds = rewardTypes.map(reward => reward.rewardTypeId);
      setSelectedRewards(allRewardIds);
    } else {
      setSelectedRewards(value);
    }
  }, [rewardTypes]);

  // Memoize the date change handlers
  const handleStartDateChange = useCallback((date: Dayjs | null) => {
    setStartDate(date);
  }, []);

  const handleEndDateChange = useCallback((date: Dayjs | null) => {
    setEndDate(date);
  }, []);

  // Define the columns you want to display and their order
  const DESIRED_COLUMNS = [
    'ApprovalId',
    'RewardName',
    'ValueName',
    'RewardDate',
    'RewardedAmount',
    'RewardDescription',
    'NominationDate',
    'NomineeName',
    'NomineeEmail',
    'NomineeEmployeeId',
    'NomineeLegalEntityName',
    'NomineeAccountStatus',
    'NomineeCountry',
    'NomineeCity',
    'NomineeProductName',
    'NomineeDesignation',
    'NomineeGrade',
    'NomineeUnit',
    'NominatorName',
    'NominatorEmail',
    'NominatorEmployeeId',
    'NominatorLegalEntityName',
    'NominatorAccountStatus',
    'NominatorCountry',
    'NominatorCity',
    'NominatorProductName',
    'NominatorDesignation',
    'NominatorGrade',
    'NominatorUnit',
    'FormFields',
    'Citation',
    'TeamMembers',
    'TeamName',
    'Approval1UserName',
    'Approval1EmailId',
    'Approval1Status',
    'Approval1Comments',
    'Approval2UserName',
    'Approval2EmailId',
    'Approval2Status',
    'Approval2Comments',
  ];

  // Function to format column names with spaces
  const formatColumnName = useCallback((columnName: string) => {
    // Special case for RewardedAmount
    if (columnName === 'RewardedAmount') {
      return 'Rewarded Amount In USD';
    }
    
    // Add space before capital letters (camelCase to spaced text)
    return columnName.replace(/([A-Z])/g, ' $1').trim();
  }, []);

  // Function to format date to dd-MMM-yyyy format
  const formatDateValue = useCallback((dateValue: any) => {
    if (!dateValue) return '';
    
    try {
      let date: Date;
      
      // Handle different date formats that might come from Excel
      if (typeof dateValue === 'number') {
        // Excel serial date number
        date = new Date((dateValue - 25569) * 86400 * 1000);
      } else if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else {
        return dateValue; // Return as-is if can't parse
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return dateValue; // Return original value if invalid date
      }

      // Format to dd-MMM-yyyy
      const day = date.getDate().toString().padStart(2, '0');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      
      return `${day}-${month}-${year}`;
    } catch (error) {
      console.warn('Error formatting date:', dateValue, error);
      return dateValue; // Return original value if error
    }
  }, []);

  // Function to format currency values
  const formatCurrencyValue = useCallback((amount: any) => {
    // Return empty string for null, undefined, or empty values
    if (amount === null || amount === undefined || amount === '') return '';
    
    try {
      // Convert to number if it's a string
      const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
      
      // Check if it's a valid number
      if (isNaN(numericAmount)) {
        return ''; // Return empty string if not a valid number
      }

      // Format as number with 2 decimal places and thousands separator (no currency symbol)
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numericAmount);
    } catch (error) {
      console.warn('Error formatting currency:', amount, error);
      return ''; // Return empty string if error
    }
  }, []);

  // Function to process and reorder Excel data
  const processExcelData = useCallback((data: any[]) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      console.log('No data to process or data is not an array:', data);
      return [];
    }

    // Log the first row to see available columns
    if (data.length > 0) {
      console.log('Available columns:', Object.keys(data[0]));
    }

    // Define which columns should be formatted as dates
    const DATE_COLUMNS = ['RewardDate', 'NominationDate'];
    // Define which columns should be formatted as currency
    const CURRENCY_COLUMNS = ['RewardedAmount'];

    return data.map(row => {
      const processedRow: any = {};
      
      // Always include all desired columns in the specified order, even if null/undefined
      DESIRED_COLUMNS.forEach(column => {
        // Use formatted column name as the key for display
        const formattedColumnName = formatColumnName(column);
        
        // Get the value, defaulting to empty string if null/undefined/missing
        const value = row.hasOwnProperty(column) ? row[column] : '';
        
        // Apply date formatting for specific date columns
        if (DATE_COLUMNS.includes(column)) {
          processedRow[formattedColumnName] = formatDateValue(value);
        } 
        // Apply currency formatting for specific currency columns
        else if (CURRENCY_COLUMNS.includes(column)) {
          processedRow[formattedColumnName] = formatCurrencyValue(value);
        } 
        else {
          processedRow[formattedColumnName] = value || '';
        }
      });
      
      return processedRow;
    });
  }, [formatColumnName, formatDateValue, formatCurrencyValue]);

  const handleDownload = useCallback(async () => {
    if (selectedRewards.length === 0) {
      message.warning('Please select at least one reward type.');
      return;
    }

    if (!startDate) {
      message.warning('Please select a start date.');
      return;
    }

    if (!endDate) {
      message.warning('Please select an end date.');
      return;
    }

    if (!endDate.isAfter(startDate, 'day')) {
      message.warning('End date must be greater than start date.');
      return;
    }

    setDownloading(true);
    try {
      const token = await fetchToken();
      
      // Prepare request payload with date range if provided
      const requestPayload: any = {
        rewardIds: selectedRewards
      };

      if (startDate) {
        requestPayload.startDate = startDate.format('YYYY-MM-DD');
      }

      if (endDate) {
        requestPayload.endDate = endDate.format('YYYY-MM-DD');
      }

      const response = await axios.post(
        `${API_URL}/api/RewardsReports/download-reward-nominations`,
        requestPayload,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'arraybuffer', // Get as arraybuffer to read Excel file
        }
      );


      if (!response.data || response.data.byteLength === 0) {
        message.info('No data available for the selected criteria.');
        return;
      }
      // Read the Excel file from the response
      // Check if response data is empty (null case from backend)
      const workbook = XLSX.read(response.data, { type: 'array' });
      const worksheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[worksheetName];
      
      // Convert Excel data to JSON
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      // Process the data to reorder columns and remove unwanted ones
      const processedData = processExcelData(jsonData);
      // Create a new workbook with the processed data
      const newWorksheet = XLSX.utils.json_to_sheet(processedData);
      const newWorkbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(newWorkbook, newWorksheet, 'Reward Nominations');

      // Generate buffer
      const excelBuffer = XLSX.write(newWorkbook, { bookType: 'xlsx', type: 'array' });
      
      // Create blob and download
      const blob = new Blob([excelBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'RewardNominations.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Clean up the URL to free memory
      window.URL.revokeObjectURL(url);

      message.success('Report downloaded successfully');
    } catch (error) {
      console.error('Error downloading the report:', error);
      message.error('Failed to download report. Please try again.');
    } finally {
      setDownloading(false);
    }
  }, [selectedRewards, startDate, endDate, fetchToken, processExcelData]);

  return (
    <div>
      <Spin spinning={loading} tip="Loading reward types...">
        <Select
          mode="multiple"
          style={{ width: '100%', marginBottom: '20px' }}
          placeholder="Select reward types"
          value={selectedRewards.length === rewardTypes.length && rewardTypes.length > 0 ? ['all'] : selectedRewards}
          onChange={handleSelectionChange}
          showSearch
          optionFilterProp="children"
          filterOption={filterOption}
          disabled={loading}
          notFoundContent={loading ? 'Loading...' : 'No data'}
        >
          {rewardOptions}
        </Select>
      </Spin>

      <Row gutter={16} style={{ marginBottom: '20px' }}>
        <Col span={12}>
          <DatePicker
            style={{ width: '100%' }}
            placeholder="Select nomination start date"
            value={startDate}
            onChange={handleStartDateChange}
            format="DD-MMM-YYYY"
            disabled={loading || downloading}
            //status={!startDate ? 'error' : undefined}
          />
        </Col>
        <Col span={12}>
          <DatePicker
            style={{ width: '100%' }}
            placeholder="Select nomination end date"
            value={endDate}
            onChange={handleEndDateChange}
            format="DD-MMM-YYYY"
            disabled={loading || downloading}
            disabledDate={(current) => startDate ? !current.isAfter(startDate, 'day') : false}
            //status={!endDate ? 'error' : undefined}
          />
        </Col>
      </Row>
      
      <Button 
        type="primary" 
        onClick={handleDownload}
        loading={downloading}
        disabled={loading || selectedRewards.length === 0 || !startDate || !endDate}
      >
        {downloading ? 'Downloading...' : 'Download Report'}
      </Button>
    </div>
  );
});

DownloadRewardsReport.displayName = 'DownloadRewardsReport';

export default DownloadRewardsReport;